// frontend/src/components/ShareButtons.js
// Share a Look (or any URL) via native share, copy-link, and the social
// platforms' own brand logos. External links open with noopener,noreferrer.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaShareAlt, FaLink } from "react-icons/fa";
import {
  SiWhatsapp,
  SiThreads,
  SiFacebook,
  SiX,
  SiTelegram,
} from "react-icons/si";

const pillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  padding: "6px 11px",
  fontSize: "12px",
  fontWeight: "600",
  borderRadius: "999px",
  border: "none",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const logoBtnStyle = (color) => ({
  width: "28px",
  height: "28px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  background: color,
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontSize: "13px",
  flexShrink: 0,
});

function ShareButtons({ url, imageUrl = "", title = "", text = "" }) {
  const [copied, setCopied] = useState(""); // "" | "link" | "instagram" | "threads"
  const [canShare, setCanShare] = useState(false);
  const [canShareImage, setCanShareImage] = useState(false);
  // Shown briefly when a tap could not hand the photo to the share sheet, so the customer
  // - and whoever is debugging this - can tell WHY. Without it a tap that cannot work is
  // indistinguishable from one that did.
  const [shareNote, setShareNote] = useState("");
  // Set when the note needs somewhere to go. A failed share sheet is the one case the tap
  // cannot finish by itself, so the note has to offer the link by hand - a plain anchor,
  // which unlike window.open() is never popup-blocked.
  const [noteAction, setNoteAction] = useState(null); // { href, label } | null
  // The card, once fetched. State rather than a ref because whether a photo can be sent has
  // to be known at RENDER time: the handler must decide synchronously whether to let the
  // anchor navigate, and anything decided after an await is too late.
  const [photoFile, setPhotoFile] = useState(null);
  // Why the last load attempt failed. Surfaced in the note, because "couldn't prepare the
  // photo" cannot tell an empty URL from a blocked request from a bad status - and that
  // difference is a bug here versus the browser refusing.
  const imageErrorRef = useRef("");
  const shareText = text || title || "Check out this look on OGESTYLE";
  const captionWithLink = `${shareText} ${url}`.trim();
  const threadsComposerHref = `https://www.threads.com/intent/post?text=${encodeURIComponent(captionWithLink)}`;
  // Named in the fallback note. "Couldn't load the photo" is useless for telling a wrong
  // host from a blocked request from a 404, and that difference is a bug here versus the
  // device refusing.
  const imageHost = (() => {
    try {
      return imageUrl ? new URL(imageUrl).host : "no image URL";
    } catch (err) {
      return "unparseable image URL";
    }
  })();
  const noteTimerRef = useRef(null);

  // A note is the only feedback these buttons give, so make sure the previous one cannot
  // clear the new one early, and that no timer outlives the page.
  const flashNote = (message, action = null) => {
    setShareNote(message);
    setNoteAction(action);
    if (noteTimerRef.current) clearTimeout(noteTimerRef.current);
    noteTimerRef.current = setTimeout(() => {
      setShareNote("");
      setNoteAction(null);
    }, 6000);
  };

  useEffect(
    () => () => {
      if (noteTimerRef.current) clearTimeout(noteTimerRef.current);
    },
    [],
  );

  const loadImageFile = useCallback(async () => {
    const fail = (reason) => {
      imageErrorRef.current = reason;
      return null;
    };
    if (!imageUrl) return fail("no image URL for this look");
    try {
      const res = await fetch(imageUrl, { mode: "cors" });
      if (!res.ok) return fail(`the image server returned ${res.status}`);
      const blob = await res.blob();
      if (!blob.size) return fail("the image came back empty");
      imageErrorRef.current = "";
      return new File([blob], "ogestyle-look.jpg", {
        type: blob.type || "image/jpeg",
      });
    } catch (err) {
      // "Failed to fetch" here nearly always means CORS or the network.
      return fail(err && err.message ? err.message : "the image request failed");
    }
  }, [imageUrl]);

  // Native share (navigator.share) is mobile-only — show the Share pill only
  // where it actually opens the OS share sheet, so desktop never sees a dead
  // button. Most mobile users share this way.
  //
  // The image buttons need more than that: only some browsers can hand a FILE to the
  // share sheet, and that is the only way a photo reaches Instagram or Threads, because
  // neither offers a web URL that pre-loads one. Probe with a real File so the buttons
  // fall back to copy-link where it cannot work, instead of offering a tap that does
  // nothing.
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    if (typeof navigator.share === "function") {
      setCanShare(true);
    }
    // Desktop is excluded on purpose. Chrome on Windows answers canShare({files}) with
    // true, but the only thing behind it is the Windows share dialog, which lists no
    // Instagram and no Threads - the customer taps and gets a dead sheet instead of the
    // link. Anything with a touch screen is treated as a phone or tablet, which is what
    // the share sheet exists for.
    //
    // Deliberately generous, and that matters: this check first shipped as
    // `(pointer: coarse)` alone, and an over-strict gate silently removes the feature
    // rather than breaking it visibly.
    const hasTouch =
      (navigator.maxTouchPoints || 0) > 0 ||
      (typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(pointer: coarse)").matches);
    if (!hasTouch) return;

    // Deliberately NOT gated on navigator.canShare({ files }).
    //
    // canShare is a report, not a guarantee, and when it is wrong it is wrong in the
    // expensive direction: a false negative silently deletes the feature instead of
    // breaking it visibly. That is what happened here - the share was never attempted at
    // all on a phone that could very likely have done it.
    //
    // So the capability question is left to the attempt itself: try navigator.share with
    // the file, and fall back only when it actually fails.
    if (typeof navigator.share === "function" && typeof File === "function") {
      setCanShareImage(true);
    }
  }, []);

  // Fetch the card BEFORE the tap so the share sheet opens from a synchronous call.
  //
  // Awaiting the fetch first and then calling navigator.share() is not reliably allowed:
  // iOS requires transient user activation and the await can consume it, so the button
  // ends up only ever copying a link. Fetching ahead also means the sheet opens at once
  // instead of after a ~55KB download.
  useEffect(() => {
    if (!imageUrl || !canShareImage) return undefined;
    let cancelled = false;

    (async () => {
      const file = await loadImageFile();
      if (cancelled || !file) return;
      setPhotoFile(file);
    })();

    return () => {
      cancelled = true;
    };
  }, [imageUrl, canShareImage, loadImageFile]);

  // Copy is the whole fallback story for Instagram, so it must not be the step that
  // fails. navigator.clipboard is absent on insecure origins and can be refused outright,
  // hence the plain <textarea> path behind it.
  const legacyCopy = (value) => {
    try {
      const el = document.createElement("textarea");
      el.value = value;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.top = "-1000px";
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch (err) {
      return false;
    }
  };

  const writeClipboard = (value) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).catch(() => legacyCopy(value));
        return true;
      }
    } catch (err) {
      // Fall through to the legacy path.
    }
    return legacyCopy(value);
  };

  const flashCopied = (label) => {
    setCopied(label);
    setTimeout(() => setCopied(""), 2600);
  };

  const copyLink = (label) => {
    writeClipboard(url);
    flashCopied(label);
  };

  // The native sheet carries the photo when the card is already in hand, and falls back to
  // text + link when it is not - a device that refuses the file must not get a dead button.
  const nativeShare = async () => {
    const file = photoFile;
    if (file) {
      try {
        await navigator.share({
          files: [file],
          title,
          text: captionWithLink,
        });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return; // dismissed on purpose
      }
    }
    try {
      await navigator.share({ title, text: shareText, url });
    } catch (err) {
      // User dismissed the share sheet — ignore.
    }
  };

  // Instagram and Threads.
  //
  // Instagram is the platform where the PHOTO is the whole point, and the OS share sheet is
  // the only door a web page has into it - Instagram exposes no share endpoint, and it
  // discards any caption travelling with a shared photo. So a tap does both halves at once:
  //
  //   1. caption + link go on the clipboard, because Instagram will not take them with the
  //      image and the customer otherwise has to retype them;
  //   2. the share sheet opens with the card, so they pick the photo and paste the caption.
  //
  // Threads takes pre-filled text, so its composer carries the link on its own and the
  // share sheet is the photo path on top of that.
  //
  // Whether a photo can be sent is decided at RENDER time, never after an await: the handler
  // has to know synchronously whether to preventDefault(). Awaiting a fetch in the handler
  // and then calling window.open() is how this broke before - the await consumed the tap
  // gesture, the popup was blocked, and the button appeared to do nothing at all.
  //
  // canShare is deliberately NOT consulted, and that is the whole point of the next line.
  //
  // The share is attempted whenever there is a photo to send - the capability question is
  // left to the attempt itself, because canShare is a report, not a guarantee, and when it
  // is wrong it is wrong in the expensive direction: a false "no" silently swaps the share
  // sheet for a plain app open, which is exactly what got reported (Instagram opening with
  // no sheet). A false "yes" merely falls through to the note and the link it carries.
  const canSendPhoto =
    Boolean(photoFile) &&
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function";

  const sharePhotoThenOpen = (e, fallbackHref, platform) => {
    writeClipboard(captionWithLink);
    flashCopied(platform.toLowerCase());

    if (!canSendPhoto) {
      // No photo to send. Say WHICH half is missing - the note is the only diagnostic this
      // button has, and "did nothing useful" must not be ambiguous between a fetch that
      // failed and a surface that never had an image URL to begin with.
      const why = photoFile
        ? "This browser has no share sheet"
        : `Photo not ready (${imageErrorRef.current || "still loading"}) on ${imageHost}`;
      flashNote(`${why} — caption + link copied, opening ${platform}.`);
      return;
    }

    e.preventDefault();
    const offerLink = (why) =>
      flashNote(`${why} — caption + link copied.`, {
        href: fallbackHref,
        label: `Open ${platform}`,
      });

    try {
      navigator
        .share({ files: [photoFile], title, text: captionWithLink })
        .then(() => flashNote("Caption + link copied — paste it into the post."))
        .catch((err) => {
          // Dismissing the sheet is a decision, not a failure.
          if (err && err.name === "AbortError") return;
          offerLink(
            `${platform} wouldn't take the photo (${err && err.name ? err.name : "error"})`,
          );
        });
    } catch (err) {
      // Thrown synchronously when the browser has navigator.share but will not take files.
      offerLink(`${platform} wouldn't take the photo (${err && err.name ? err.name : "error"})`);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        flexWrap: "wrap",
      }}
    >
      {canShare && (
        <button
          onClick={nativeShare}
          style={{ ...pillStyle, background: "#1e40af", color: "#fff" }}
          title="Share"
        >
          <FaShareAlt /> Share
        </button>
      )}
      <button
        onClick={() => copyLink("link")}
        style={{
          ...pillStyle,
          background: copied === "link" ? "#dcfce7" : "#f3f4f6",
          color: copied === "link" ? "#15803d" : "#374151",
        }}
        title={copied === "link" ? "Copied!" : "Copy link"}
      >
        <FaLink /> {copied === "link" ? "Copied!" : "Copy link"}
      </button>
      {/*
        Instagram is deliberately absent.

        It exposes no share endpoint, so all the button could ever do was open
        instagram.com - which on a phone becomes the app with an empty composer, and no
        photo. A button that promises sharing and delivers a blank screen is worse than no
        button, so it is hidden rather than left there looking broken.

        Nothing was deleted to make this reversible: sharePhotoThenOpen still takes a
        fallbackHref, so restoring it means putting an anchor back that calls it.
      */}
      <a
        href={threadsComposerHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => sharePhotoThenOpen(e, threadsComposerHref, "Threads")}
        style={logoBtnStyle("#000000")}
        title={
          copied === "threads"
            ? "Caption + link copied"
            : "Post this look to Threads"
        }
        aria-label="Threads"
      >
        <SiThreads />
      </a>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(captionWithLink)}`}
        target="_blank"
        rel="noopener noreferrer"
        style={logoBtnStyle("#25D366")}
        title="WhatsApp"
        aria-label="WhatsApp"
      >
        <SiWhatsapp />
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
        style={logoBtnStyle("#1877F2")}
        title="Facebook"
        aria-label="Facebook"
      >
        <SiFacebook />
      </a>
      <a
        href={`https://x.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`}
        target="_blank"
        rel="noopener noreferrer"
        style={logoBtnStyle("#000000")}
        title="X (Twitter)"
        aria-label="Share on X"
      >
        <SiX />
      </a>
      <a
        href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`}
        target="_blank"
        rel="noopener noreferrer"
        style={logoBtnStyle("#0088cc")}
        title="Telegram"
        aria-label="Telegram"
      >
        <SiTelegram />
      </a>
      {shareNote && (
        <span
          role="status"
          style={{
            flexBasis: "100%",
            textAlign: "center",
            fontSize: "12px",
            color: "#6b7280",
            marginTop: "4px",
          }}
        >
          {shareNote}
          {noteAction && (
            <>
              {" "}
              <a
                href={noteAction.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#1e40af",
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                {noteAction.label}
              </a>
            </>
          )}
        </span>
      )}
    </div>
  );
}

export default ShareButtons;
